import React, { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import Button from "../components/Button";
import { DateTime } from "luxon";
import {
  Header,
  HeadMetaTags,
  ImageCarousel,
  LoadingSpinner,
  NavigateBack,
  RoomIcons,
  ShareButton,
} from "../components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStateContext } from "../contexts/ContextProvider";
import { FaCamera, FaYahoo } from "react-icons/fa";
import { IoIosHeart } from "react-icons/io";
import { ImCopy } from "react-icons/im";
import { BiLogoGmail } from "react-icons/bi";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { SiAol } from "react-icons/si";
import { ImSpinner9 } from "react-icons/im";
import noImage from "../data/images/no-image4.jpeg";
import {
  apiCreateEmailRelay,
  apiGetPublishedRoomById,
} from "../services/apiService";

const Room = () => {
  const { isLoggedIn, userLikesArray, setUserLikesArray, publishedRooms } =
    useStateContext();

  const [room, setRoom] = useState(null);
  const [isLoading, setIsloading] = useState(true);
  const [isRelayLoading, setIsRelayLoading] = useState(false);
  const [relayAddress, setRelayAddress] = useState("");
  const [emailLinks, setEmailLinks] = useState();
  const [isCopied, setIsCopied] = useState(false);
  const [relayError, setRelayError] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const searchParams = new URLSearchParams(location.search);
  const scrollLocation = searchParams.get("position");
  const currentPath = location.pathname + location.search;

  const isTracking =
    process.env.REACT_APP_GA_TRACKING === "true" ? true : false;

  useEffect(() => {
    if (relayAddress) {
      buildLinks();
    }
  }, [relayAddress]);

  useEffect(() => {
    if (isTracking) {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname + location.search,
      });
    }
    // eslint-disable-next-line
  }, [location]);

  const handleGAEvent = (category, action, label) => {
    if (isTracking) {
      ReactGA.event({
        category,
        action,
        label,
      });
    }
  };

  useEffect(() => {
    const searchedRoom = publishedRooms.find((x) => x.roomUuid === params.uuid);
    if (searchedRoom) {
      setRoom(searchedRoom);
    } else {
      getPublishedRoomById(params.uuid);
    }
    if (scrollLocation) {
      document.getElementById(scrollLocation)?.scrollIntoView();
    }
    setIsloading(false);
    // eslint-disable-next-line
  }, []);

  const getPublishedRoomById = async (uuid) => {
    const response = await apiGetPublishedRoomById(uuid);
    if (response.hasError) {
      console.log("Get published room error!!");
      navigate("error-404");
    } else {
      setRoom(response.data);
    }
  };

  const updateLiked = (id) => {
    if (userLikesArray.find((x) => x === id)) {
      setUserLikesArray(
        userLikesArray.toSpliced(
          userLikesArray.findIndex((x) => x === id),
          1
        )
      );
    } else {
      setUserLikesArray((prevState) => [...prevState, id]);
      handleGAEvent("Liked", "Room Liked", `Published Room Id: ${id}`);
    }
  };

  const generateEmailRelay = async () => {
    setIsRelayLoading(true);
    try {
      const response = await apiCreateEmailRelay(room.roomUuid);
      if (response.hasError) {
        setRelayError(true);
        setTimeout(() => setRelayError(false), 3500);
      } else {
        setTimeout(() => {
          setRelayAddress(response.data.relay);
          setIsRelayLoading(false);
        }, 1000);
      }
    } catch (err) {
      setRelayError(true);
    }
  };

  const buildLinks = () => {
    // const baseUrl = process.env.REACT_APP_BASE_URL;
    // const fullPath = baseUrl + currentPath;
    // const body = encodeURIComponent(`\n\n--\nOriginal Post:\n${fullPath}`);
    const subject = encodeURIComponent(room.shortDesc);
    if (relayAddress) {
      setEmailLinks({
        default: `mailto:${relayAddress}@relay.snappily.io?subject=${subject}`,
        gmail: `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&to=${relayAddress}%40relay.snappily.io`,
        yahoo: `http://compose.mail.yahoo.com/?subj=${subject}&to=${relayAddress}%40relay.snappily.io`,
        outlook: `https://outlook.live.com/default.aspx?rru=compose&subject=${subject}&to=${relayAddress}%40relay.snappily.io`,
        aol: `http://mail.aol.com/mail/compose-message.aspx?subject=${subject}&to=${relayAddress}%40relay.snappily.io`,
      });
    }
  };

  const handleCopy = async () => {
    const textToCopy = `${relayAddress}@relay.snappily.io`;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      clearTimeout();
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      {room && (
        <div className="w-screen overflow-y-scroll overflow-hidden">
          <HeadMetaTags
            currentPath={currentPath}
            title={`$${room.rent} - Room for rent in ${room.propertyCity}, ${room.propertyState}`}
            description={
              room.longDesc.length > 155
                ? room.longDesc.slice(0, 152) + "..."
                : room.longDesc
            }
            ogTitle={`$${room.rent} - Room for rent in ${room.propertyCity}, ${room.propertyState}`}
            ogImage={room.images[0]}
          />
          <section id="header" className="w-screen">
            <Header />
            <NavigateBack zIndex="z-20" />
          </section>
          <section
            id="images"
            className="mt-[120px] border-snappily-orange border-b-4 relative"
          >
            <div className="flex justify-center">
              <ImageCarousel images={room.images} type="room" />
              {room.images.length > 0 && (
                <div
                  className="absolute top-2.5 left-3 flex items-center text-white bg-black 
                opacity-40 px-1.5 z-10"
                >
                  <FaCamera className="" />
                  <div className="ml-1 mt-0.5">{room.images.length}</div>
                </div>
              )}
              <IoIosHeart
                className={`absolute top-3 right-3 hover:text-red-300 
                  text-3xl z-10 cursor-pointer
                  ${
                    userLikesArray.find((x) => x === room.id)
                      ? "text-red-500 "
                      : "text-slate-200"
                  }`}
                onClick={() => {
                  updateLiked(room.id);
                }}
              />
            </div>
          </section>
          <section id="description" className="px-5">
            <div className="flex justify-around w-full">
              <div className="flex justify-between items-center w-full my-2 capitalize">
                <div className="text-xl text-green-500">
                  ${Intl.NumberFormat().format(room.rent)}
                </div>
                <div className="flex">
                  <div className="text-lg">
                    {room.propertyCity}, {room.propertyState} {room.propertyZip}
                  </div>
                  <ShareButton
                    title="Snappily - Roommates Made Simple!"
                    text={room.shortDesc}
                    url={currentPath}
                  />
                </div>
              </div>
            </div>
            <div
              className="w-full whitespace-pre-line text-black text-2xl capitalize"
              // onClick={() => navigate(`/rooms/${item.id}`)}
            >
              {room.shortDesc.toLowerCase()}
            </div>
            <div className="flex justify-center bg-gray-200 rounded-2xl mt-3 px-5">
              <div className="text-2xl text-snappily-orange py-3 px-1 cursor-pointer">
                <RoomIcons currentRoom={room} />
              </div>
            </div>
            <div className="text-xl mt-3">Description</div>
            <div
              className="w-full whitespace-pre-line text-justify text-black text-base
               bg-gray-200 rounded-2xl hyphens-auto p-3 mt-3"
            >
              {room.longDesc}
            </div>
            <div className="text-xl mt-10">Amenities</div>
            <div className="mt-5 mx-5">
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Furnished:</div>
                <div className="place-self-end">
                  {room.isFurnished ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Private Bathroom:</div>
                <div className="place-self-end">
                  {room.privateBath ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Off Street Parking:</div>
                <div className="place-self-end">
                  {room.parking ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Wifi Available:</div>
                <div className="place-self-end">{room.wifi ? "Yes" : "No"}</div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Laundry On-Site:</div>
                <div className="place-self-end">
                  {room.laundry ? "Yes" : "No"}
                </div>
              </div>
            </div>
            <div className="text-xl mt-10">Other Details</div>
            <div className="mt-5 mx-5">
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Available:</div>
                <div className="place-self-end">
                  {DateTime.fromISO(room.available).toFormat("dd-MMM-yyyy")}
                </div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Security Deposit:</div>
                <div className="place-self-end">
                  ${Intl.NumberFormat().format(room.securityDep)}
                </div>
              </div>
              {/* <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Other Roommates:</div>
                <div className="place-self-end">
                  {rooms.filter((x) => x.propertyId === room.propertyId)
                    .length - 1}
                </div>
              </div> */}
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Genders Allowed:</div>
                {room.femaleOnly && (
                  <div className="place-self-end">Female Only</div>
                )}
                {room.maleOnly && (
                  <div className="place-self-end">Male Only</div>
                )}
                {room.isCoEd && (
                  <div className="place-self-end">Mixed Gender</div>
                )}
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Smoking Allowed:</div>
                <div className="place-self-end">
                  {room.smokingOk ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Cannabis Allowed:</div>
                <div className="place-self-end">
                  {room.cannabisOk ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Dogs Allowed:</div>
                <div className="place-self-end">
                  {room.dogsOk ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Cats Allowed:</div>
                <div className="place-self-end">
                  {room.catsOk ? "Yes" : "No"}
                </div>
              </div>
              {room.lgbtFriendly && (
                <div className="flex justify-between odd:bg-gray-100 p-2">
                  <div>LGBTQ+ Friendly:</div>
                  <div className="place-self-end">Yes</div>
                </div>
              )}
              <div className="flex justify-between odd:bg-gray-100 p-2">
                <div>Wheelchair Accessible:</div>
                <div className="place-self-end">
                  {room.isAccessible ? "Yes" : "No"}
                </div>
              </div>
            </div>
            <div className="md:flex md:justify-center text-2xl mt-10">
              About the landlord
            </div>
            <div className="mt-5 mx-5">
              <div className="flex justify-center mt-8">
                <img
                  src={
                    room.landlordAvatar instanceof Blob
                      ? URL.createObjectURL(room.landlordAvatar)
                      : room.landlordAvatar
                      ? room.landlordAvatar
                      : noImage
                  }
                  alt={`${room.landlordName}`}
                  className="h-20 w-20 object-cover rounded-full drop-shadow-2xl"
                />
              </div>
              <div className="grid justify-items-center">
                <div className="flex justify-center text-xl mt-3">
                  {room.landlordName}
                </div>
                <div
                  className="whitespace-pre-line max-w-[500px] text-justify bg-gray-200 
                rounded-2xl hyphens-auto p-3 mt-3"
                >
                  {room.landlordBio}
                </div>
              </div>
            </div>
            <div
              id="message"
              className="md:flex md:justify-center text-2xl mt-10"
            >
              Send a message
            </div>
            {!isLoggedIn ? (
              <div
                className={`flex justify-center mt-5 ${
                  emailLinks ? "mb-20" : "mb-[310px]"
                }`}
              >
                <div className=" w-80 border-gray-300 border-1 rounded-xl p-5">
                  <div className="grid justify-items-center">
                    <div className="grid justify-items-center pt-2">
                      <Button
                        background="bg-snappily-orange"
                        text="text-white"
                        opacity="bg-opacity-100"
                        title="Reply"
                        height="h-10"
                        width="w-40"
                        borderHover="hover:border-snappily-orange"
                        isDisabled={emailLinks}
                        customFunc={() => {
                          generateEmailRelay();
                          handleGAEvent(
                            "Button Click",
                            "Click",
                            "Email Relay Generated"
                          );
                        }}
                      />
                      {!emailLinks && (
                        <div className="pl-1 mt-2">No account required.</div>
                      )}
                    </div>
                    {isRelayLoading && (
                      <div className="text-4xl text-snappily-orange animate-spin mt-14 mb-14">
                        <ImSpinner9 />
                      </div>
                    )}
                    {emailLinks && (
                      <div className="grid justify-items-left mt-5">
                        <div className="grid">
                          <div className="flex justify-center">Reply to:</div>
                          <div className="flex justify-center items-center gap-x-3 mt-2">
                            <a
                              className="text-blue-500 hover:text-blue-700 cursor-pointer"
                              href={emailLinks.default}
                            >
                              {`${relayAddress.slice(
                                0,
                                4
                              )}...@relay.snappily.io`}
                            </a>
                            <button
                              className="flex items-center gap-x-1 text-blue-500 border-1 border-blue-500 
                                rounded-md hover:text-snappily-orange hover:border-snappily-orange p-1"
                              onClick={handleCopy}
                            >
                              {isCopied ? (
                                "Copied!"
                              ) : (
                                <>
                                  copy
                                  <ImCopy />
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="grid justify-items-left">
                          <div className="mt-5">Other:</div>
                          <div className="flex items-center gap-x-1">
                            <BiLogoGmail className="text-4xl text-red-400" />
                            <a
                              className="text-blue-500 hover:text-blue-700 cursor-pointer"
                              href={emailLinks.gmail}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Gmail
                            </a>
                          </div>
                          <div className="flex items-center gap-x-1">
                            <FaYahoo className="text-4xl text-[#5F01D1]" />
                            <a
                              className="text-blue-500 hover:text-blue-700 cursor-pointer"
                              href={`mailto:${relayAddress}@relay.snappily.io`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Yahoo
                            </a>
                          </div>
                          <div className="flex items-center gap-x-1">
                            <PiMicrosoftOutlookLogoFill className="text-4xl text-[#0072C6]" />
                            <a
                              className="text-blue-500 hover:text-blue-700 cursor-pointer"
                              href={`mailto:${relayAddress}@relay.snappily.io`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Hotmail, Outlook, Live
                            </a>
                          </div>
                          <div className="flex items-center gap-x-1">
                            <SiAol className="text-4xl" />
                            <a
                              className="text-blue-500 hover:text-blue-700 cursor-pointer"
                              href={`mailto:${relayAddress}@relay.snappily.io`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              AOL Mail
                            </a>
                          </div>
                        </div>
                        <div className=" text-center text-sm text-gray-500 mt-5">
                          *Your email address will NOT be shared with recipient.
                        </div>
                      </div>
                    )}
                    {relayError && (
                      <>
                        <div className="flex justify-center text-red-500 mt-5">
                          🚨 Slow down! 🚨
                        </div>
                        <div className="flex justify-center text-red-500 mt-2">
                          You're sending too many requests.
                        </div>
                        <div className="flex justify-center text-red-500 mt-2">
                          Take a coffee break... ☕️ 🍩
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-5 mb-40">
                <div className="grid justify-items-center realtive mt-5">
                  <div className="grid content-center h-60 w-80 border-gray-300 border-1 rounded-xl pb-5">
                    <div className="flex justify-center">
                      This is your listing.
                    </div>
                    <div className="flex justify-center">
                      You cannot message yourself.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default Room;
